import React, { useEffect, useRef, useState } from 'react';
import { getEnvoiConfig } from '../lib/envoiConfig';
import { fetchTwelveLabsVideo, resolveTwelveLabsIds } from '../lib/twelvelabs';

function formatMS(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
}

// Renders a muted, looping inline preview of a search-result clip while the
// card is hovered — mounted/unmounted by the parent via the `active` prop.
export default function HoverClipPreview({ item, active }) {
    const { playerKey, analyticsKey } = getEnvoiConfig();
    const containerRef = useRef(null);
    const playerRef = useRef(null);
    const progressBarRef = useRef(null);
    const [video, setVideo] = useState(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);

    const { indexId, videoId } = resolveTwelveLabsIds(item || {});
    const start = item?.start ?? 0;
    const end = item?.end ?? start;
    const clipDuration = Math.max(0.001, end - start);

    useEffect(() => {
        if (!active || video || !indexId || !videoId) return;
        fetchTwelveLabsVideo(indexId, videoId)
            .then(setVideo)
            .catch((err) => console.error('❌ HoverClipPreview: video fetch failed:', err));
    }, [active, indexId, videoId]);

    useEffect(() => {
        const hlsUrl = video?.hls?.video_url;
        if (!active || !hlsUrl || !containerRef.current) return;
        if (!window.bitmovin?.player) {
            console.error('❌ HoverClipPreview: Bitmovin SDK not loaded');
            return;
        }

        const Player = window.bitmovin.player.Player;
        const PlayerEvent = window.bitmovin.player.PlayerEvent;
        const player = new Player(containerRef.current, {
            key: playerKey,
            analytics: { key: analyticsKey },
            ui: false,
            playback: { autoplay: true, muted: true },
        });
        playerRef.current = player;

        // Hovering briefly then moving away destroys the player while .load()
        // is still in flight, which rejects with a "cancelled" error — that's
        // expected, not a real failure, so don't log it once we've torn down.
        let destroyed = false;

        player
            .load({ hls: hlsUrl, options: { startOffset: start } })
            .then(() => {
                player.seek(start);
                player.on(PlayerEvent.Play, () => setIsPlaying(true));
                player.on(PlayerEvent.Paused, () => setIsPlaying(false));
                player.on(PlayerEvent.Muted, () => setIsMuted(true));
                player.on(PlayerEvent.Unmuted, () => setIsMuted(false));
                player.on(PlayerEvent.TimeChanged, (e) => {
                    setCurrentTime(e.time);
                    if (e.time >= end) player.seek(start);
                });
                player.on(PlayerEvent.Error, (e) => console.error('❌ HoverClipPreview: player error:', e.code, e.message));
            })
            .catch((err) => {
                if (!destroyed) console.error('❌ HoverClipPreview: player load failed:', err);
            });

        return () => {
            destroyed = true;
            try {
                player.destroy();
            } catch (e) {
                // ignore
            }
            playerRef.current = null;
            // A fresh player is always created muted (autoplay policy), so
            // reset local UI state to match — otherwise re-hovering this card
            // shows a stale "unmuted" icon for a player that's actually muted,
            // and the mute toggle button ends up doing the wrong thing.
            setIsMuted(true);
            setIsPlaying(true);
            setCurrentTime(0);
        };
    }, [active, video]);

    if (!active) return null;

    const togglePlay = (e) => {
        e.stopPropagation();
        const player = playerRef.current;
        if (!player) return;
        if (isPlaying) player.pause();
        else player.play();
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const player = playerRef.current;
        if (!player) return;
        if (isMuted) player.unmute();
        else player.mute();
    };

    const handleProgressClick = (e) => {
        e.stopPropagation();
        const player = playerRef.current;
        const bar = progressBarRef.current;
        if (!player || !bar) return;
        const rect = bar.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        player.seek(start + ratio * clipDuration);
    };

    const progressPct = Math.min(100, Math.max(0, ((currentTime - start) / clipDuration) * 100));

    return (
        <div style={{ position: 'absolute', inset: 0, zIndex: 4, background: '#000' }}>
            <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'absolute', left: 8, right: 8, bottom: 8, zIndex: 5,
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'rgba(0,0,0,0.55)', borderRadius: 8, padding: '5px 8px',
                    backdropFilter: 'blur(4px)',
                }}
            >
                <button type="button" onClick={togglePlay} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', flexShrink: 0, padding: 0 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}>
                        {isPlaying ? 'pause' : 'play_arrow'}
                    </span>
                </button>
                <button type="button" onClick={toggleMute} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', flexShrink: 0, padding: 0 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{isMuted ? 'volume_off' : 'volume_up'}</span>
                </button>
                <div
                    ref={progressBarRef}
                    onClick={handleProgressClick}
                    style={{ flex: 1, height: 12, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                >
                    <div style={{ position: 'relative', width: '100%', height: 3, borderRadius: 3, background: 'rgba(255,255,255,0.3)' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, height: 3, borderRadius: 3, background: '#7c3aed', width: `${progressPct}%` }} />
                        <div style={{
                            position: 'absolute', top: '50%', left: `${progressPct}%`,
                            width: 8, height: 8, borderRadius: '50%', background: '#fff',
                            transform: 'translate(-50%, -50%)',
                        }} />
                    </div>
                </div>
                <span style={{ fontSize: 10, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {formatMS(currentTime - start)} / {formatMS(clipDuration)}
                </span>
            </div>
        </div>
    );
}
